import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Bold, Eye, Italic, Link2, RemoveFormatting, RotateCcw, Save, Underline } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";

const FIELD_LABELS = {
    subject: "Onderwerp", preheader: "Inbox-voorbeeldtekst", greeting: "Aanhef", intro: "Intro-tekst",
    what_title: "Sectietitel", tools_title: "Sectietitel", details_title: "Sectietitel",
    lines: "Lijst-items", cards: "Kaarten", invoice_note: "Factuurnota",
    cta: "Knoptekst", outro: "Slot-tekst", signature: "Handtekening", footer: "Footer-tekst",
};
const FIELD_HINTS = {
    lines: "Eén item per regel — elk item wordt een kaartje in de mail.",
    cards: "Eén kaart per regel, formaat: Titel | Tekst",
};
const SINGLE_LINE = ["subject", "preheader", "greeting", "cta", "what_title", "tools_title", "details_title", "signature"];
const LIST_FIELDS = ["lines", "cards"];

const toEditorHtml = (value, isList) => {
    if (isList) return (value || "").split("\n").map((l) => `<div>${l || "<br>"}</div>`).join("");
    return value || "";
};

const fromEditorHtml = (el, isList) => {
    const blocks = [];
    let current = "";
    el.childNodes.forEach((n) => {
        if (n.nodeType === 1 && (n.tagName === "DIV" || n.tagName === "P")) {
            if (current.trim()) blocks.push(current);
            current = "";
            blocks.push(n.innerHTML);
        } else {
            current += n.nodeType === 1 ? n.outerHTML : n.textContent;
        }
    });
    if (current.trim()) blocks.push(current);
    const clean = blocks.map((b) => (b === "<br>" ? "" : b.trim()));
    if (isList) return clean.filter(Boolean).join("\n");
    return clean.join("<br>").replace(/(<br>)+$/, "");
};

const RichTextField = ({ value, isList, rows, onChange, testId }) => {
    const ref = useRef(null);
    useEffect(() => {
        if (ref.current && document.activeElement !== ref.current) {
            const html = toEditorHtml(value, isList);
            if (ref.current.innerHTML !== html) ref.current.innerHTML = html;
        }
    }, [value, isList]);
    const exec = (cmd, arg) => {
        ref.current?.focus();
        document.execCommand(cmd, false, arg);
        onChange(fromEditorHtml(ref.current, isList));
    };
    const tools = [
        { icon: Bold, cmd: "bold", title: "Vet" },
        { icon: Italic, cmd: "italic", title: "Cursief" },
        { icon: Underline, cmd: "underline", title: "Onderstrepen" },
    ];
    return (
        <div className="border border-zinc-200 rounded-sm bg-[#F4F4F5] focus-within:border-zinc-400">
            <div className="flex items-center gap-0.5 px-1.5 pt-1.5">
                {tools.map(({ icon: Icon, cmd, title }) => (
                    <button key={cmd} type="button" title={title} onMouseDown={(e) => { e.preventDefault(); exec(cmd); }}
                        className="h-7 w-7 inline-flex items-center justify-center rounded-sm text-zinc-500 hover:bg-white hover:text-zinc-950"
                        data-testid={`richtext-${cmd}-${testId}`}>
                        <Icon className="h-3.5 w-3.5" />
                    </button>
                ))}
                <button type="button" title="Link invoegen" onMouseDown={(e) => {
                    e.preventDefault();
                    const url = window.prompt("Link URL (bv. https://…):");
                    if (url) exec("createLink", url);
                }} className="h-7 w-7 inline-flex items-center justify-center rounded-sm text-zinc-500 hover:bg-white hover:text-zinc-950" data-testid={`richtext-link-${testId}`}>
                    <Link2 className="h-3.5 w-3.5" />
                </button>
                <button type="button" title="Opmaak wissen" onMouseDown={(e) => { e.preventDefault(); exec("removeFormat"); exec("unlink"); }}
                    className="h-7 w-7 inline-flex items-center justify-center rounded-sm text-zinc-400 hover:bg-white hover:text-zinc-950" data-testid={`richtext-clear-${testId}`}>
                    <RemoveFormatting className="h-3.5 w-3.5" />
                </button>
            </div>
            <div ref={ref} contentEditable suppressContentEditableWarning
                onInput={() => onChange(fromEditorHtml(ref.current, isList))}
                className="px-3 py-2 text-sm outline-none [&_a]:text-blue-600 [&_a]:underline"
                style={{ minHeight: `${(rows || 3) * 24}px` }}
                data-testid={`system-mail-field-${testId}`} />
        </div>
    );
};
const LANG_LABELS = { nl: "🇧🇪 NL", fr: "🇫🇷 FR", en: "🇬🇧 EN", es: "🇪🇸 ES" };

export default function AdminSystemMails() {
    const [mails, setMails] = useState([]);
    const [selKey, setSelKey] = useState(null);
    const [selLang, setSelLang] = useState("nl");
    const [values, setValues] = useState({});
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        const { data } = await api.get("/admin/system-mails");
        setMails(data.mails || []);
        if (!selKey && data.mails?.length) setSelKey(data.mails[0].key);
    };
    useEffect(() => { load().catch(() => toast.error("Laden mislukt")); }, []); // eslint-disable-line

    const mail = useMemo(() => mails.find((m) => m.key === selKey), [mails, selKey]);
    const langData = mail?.languages?.[selLang];

    useEffect(() => {
        if (!mail || !langData) return;
        const v = {};
        for (const f of mail.fields) v[f] = langData.override?.[f] ?? langData.defaults[f] ?? "";
        setValues(v);
        setPreview(null);
    }, [selKey, selLang, mails]); // eslint-disable-line

    const changedFields = () => {
        const div = document.createElement("div");
        const norm = (s) => (s || "").split("\n").map((l) => { div.innerHTML = l.trim(); return div.innerHTML; }).filter(Boolean).join("\n");
        const out = {};
        for (const f of mail?.fields || []) {
            const val = (values[f] || "").trim();
            if (!val) continue;
            if (norm(val) !== norm(langData?.defaults?.[f] || "")) out[f] = val;
        }
        return out;
    };

    const save = async () => {
        setBusy(true);
        if (selLang === "nl") toast.info("Opslaan + automatisch vertalen naar FR/EN/ES…");
        try {
            const { data } = await api.put(`/admin/system-mails/${selKey}`, { lang: selLang, fields: changedFields() });
            if (data.translated?.length) toast.success(`Opgeslagen · automatisch vertaald naar ${data.translated.map((l) => l.toUpperCase()).join("/")}`);
            else toast.success("Template opgeslagen");
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Opslaan mislukt"); }
        setBusy(false);
    };

    const reset = async () => {
        const msg = selLang === "nl"
            ? "Terug naar de standaardtekst? Dit zet ook de automatische vertalingen (FR/EN/ES) terug."
            : "Terug naar de standaardtekst voor deze taal?";
        if (!window.confirm(msg)) return;
        setBusy(true);
        try {
            await api.delete(`/admin/system-mails/${selKey}`, { params: { lang: selLang } });
            toast.success("Teruggezet naar standaard");
            await load();
        } catch { toast.error("Terugzetten mislukt"); }
        setBusy(false);
    };

    const doPreview = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/system-mails/${selKey}/preview`, { lang: selLang, fields: changedFields() });
            setPreview(data);
        } catch { toast.error("Voorbeeld mislukt"); }
        setBusy(false);
    };

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-zinc-950 font-body" data-testid="system-mails-page">
            <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8">
                <Link to="/admin/mails" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-950 mb-6" data-testid="system-mails-back">
                    <ArrowLeft className="h-4 w-4" /> Terug naar Mails
                </Link>
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">Instellingen</div>
                <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter mb-2">Mail-templates.</h1>
                <p className="text-sm text-zinc-500 mb-8 max-w-2xl">Pas de Nederlandse tekst aan en sla op — FR, EN en ES worden automatisch mee vertaald. Placeholders zoals {"{naam}"} worden automatisch ingevuld.</p>

                <div className="flex flex-wrap gap-2 mb-5" data-testid="system-mail-tabs">
                    {mails.map((m) => {
                        const customized = Object.values(m.languages || {}).some((l) => l.override);
                        return (
                            <button key={m.key} onClick={() => setSelKey(m.key)}
                                className={`h-9 px-4 text-xs font-bold rounded-sm border transition ${selKey === m.key ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`}
                                data-testid={`system-mail-tab-${m.key}`}>
                                {m.label}{customized ? " ✏️" : ""}
                            </button>
                        );
                    })}
                </div>

                {mail && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white border border-zinc-100 rounded-md p-6">
                        <div className="flex items-center gap-1.5 mb-4" data-testid="system-mail-langs">
                            {Object.keys(mail.languages).map((l) => (
                                <button key={l} onClick={() => setSelLang(l)}
                                    className={`h-8 px-3 text-xs font-bold rounded-sm border transition ${selLang === l ? "bg-[#E5A93D] text-black border-[#E5A93D]" : "bg-white text-zinc-500 border-zinc-200"}`}
                                    data-testid={`system-mail-lang-${l}`}>
                                    {LANG_LABELS[l]}{mail.languages[l].override ? " ✏️" : ""}
                                </button>
                            ))}
                        </div>
                        <p className="text-[11px] text-zinc-400 mb-4">Beschikbare placeholders: {mail.placeholders.join("  ")}</p>
                        {mail.fields.map((f) => (
                            <div key={f} className="mb-4">
                                <label className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5 block">{FIELD_LABELS[f] || f}</label>
                                {SINGLE_LINE.includes(f) ? (
                                    <Input value={values[f] || ""} onChange={(e) => setValues((v) => ({ ...v, [f]: e.target.value }))}
                                        className="bg-[#F4F4F5] border-zinc-200 rounded-sm" data-testid={`system-mail-field-${f}`} />
                                ) : (
                                    <RichTextField key={`${selKey}-${selLang}-${f}`} value={values[f] || ""} isList={LIST_FIELDS.includes(f)}
                                        rows={LIST_FIELDS.includes(f) ? 4 : 3} testId={f}
                                        onChange={(val) => setValues((v) => ({ ...v, [f]: val }))} />
                                )}
                                {FIELD_HINTS[f] && <p className="text-[10px] text-zinc-400 mt-1">{FIELD_HINTS[f]}</p>}
                            </div>
                        ))}
                        <div className="flex flex-wrap gap-2 pt-2">
                            <Button onClick={save} disabled={busy} className="h-10 px-5 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm" data-testid="system-mail-save">
                                <Save className="h-4 w-4 mr-1.5" /> Opslaan
                            </Button>
                            <Button onClick={doPreview} disabled={busy} variant="outline" className="h-10 px-5 border-zinc-300 rounded-sm font-bold" data-testid="system-mail-preview-btn">
                                <Eye className="h-4 w-4 mr-1.5" /> Voorbeeld
                            </Button>
                            {langData?.override && (
                                <Button onClick={reset} disabled={busy} variant="outline" className="h-10 px-4 border-red-200 text-red-600 hover:bg-red-50 rounded-sm font-bold" data-testid="system-mail-reset">
                                    <RotateCcw className="h-4 w-4 mr-1.5" /> Standaard
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className="bg-white border border-zinc-100 rounded-md p-4" data-testid="system-mail-preview-pane">
                        {preview ? (
                            <>
                                <p className="text-xs text-zinc-500 px-2 mb-2"><span className="font-bold text-zinc-950">Onderwerp:</span> {preview.subject}</p>
                                <iframe title="mail-voorbeeld" srcDoc={preview.html} className="w-full h-[640px] border border-zinc-100 rounded-sm bg-white" data-testid="system-mail-preview-frame" />
                            </>
                        ) : (
                            <div className="h-full min-h-[300px] flex items-center justify-center text-sm text-zinc-400">
                                Klik op "Voorbeeld" om de mail te bekijken met voorbeeldgegevens
                            </div>
                        )}
                    </div>
                </div>
                )}
            </div>
        </div>
    );
}
